import { markOwnWrite } from './ownWrites';
import { useCanvasCollabStore, type CanvasCollabMode } from '../../store/canvasCollabStore';

type SaveFn = (generation: number) => Promise<void>;
type DragActiveFn = () => boolean;
type EquipMoveSender = (equipmentId: string, position: { x: number; y: number } | null) => void;

let saveFn: SaveFn | null = null;
let dragActiveFn: DragActiveFn = () => false;
let equipMoveSender: EquipMoveSender | null = null;

let saveGeneration = 0;
let lastLocalMutationAt = 0;
let inFlight = false;
let queued = false;
const pendingWriteIds = new Set<string>();
let knownDesignIds = new Set<string>();

type PersistWorkingCopyFn = () => void;

let persistWorkingCopyFn: PersistWorkingCopyFn | null = null;

export function registerCanvasCollabRuntime(opts: {
  save: SaveFn;
  isDragActive: DragActiveFn;
  persistWorkingCopy?: PersistWorkingCopyFn;
}): void {
  saveFn = opts.save;
  dragActiveFn = opts.isDragActive;
  persistWorkingCopyFn = opts.persistWorkingCopy ?? persistWorkingCopyFn;
}

export function setEquipMoveSender(sender: EquipMoveSender | null): void {
  equipMoveSender = sender;
}

export function getCanvasCollabMode(): CanvasCollabMode {
  return useCanvasCollabStore.getState().mode;
}

export function noteLocalCanvasMutation(): void {
  lastLocalMutationAt = Date.now();
  saveGeneration += 1;
}

export function getCanvasSaveGeneration(): number {
  return saveGeneration;
}

export function getLastLocalCanvasMutationAt(): number {
  return lastLocalMutationAt;
}

export function rememberDesignIds(ids: string[]): void {
  knownDesignIds = new Set(ids);
}

export function removedDesignIds(currentIds: string[]): string[] {
  return Array.from(knownDesignIds).filter((id) => !currentIds.includes(id));
}

export function beginCollabWrite(): string {
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `w-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  pendingWriteIds.add(id);
  markOwnWrite('designs', id);
  return id;
}

export function consumePendingWriteId(id: string | null | undefined): boolean {
  if (!id) return false;
  if (pendingWriteIds.has(id)) {
    pendingWriteIds.delete(id);
    return true;
  }
  return false;
}

export function requestCanvasCommit(): void {
  persistWorkingCopyFn?.();
  if (getCanvasCollabMode() === 'default') return;
  noteLocalCanvasMutation();
  void flushCanvasSave();
}

async function flushCanvasSave(): Promise<void> {
  if (inFlight) {
    queued = true;
    return;
  }
  inFlight = true;
  const gen = saveGeneration;
  try {
    if (saveFn) await saveFn(gen);
  } catch (err) {
    console.warn('[canvasCollab] save failed', err);
  } finally {
    inFlight = false;
    if (queued) {
      queued = false;
      if (saveGeneration !== gen) {
        void flushCanvasSave();
      }
    }
  }
}

export function broadcastEquipmentMove(
  equipmentId: string,
  position: { x: number; y: number } | null
): void {
  if (getCanvasCollabMode() !== 'livePreview') return;
  equipMoveSender?.(equipmentId, position);
}

export function applyRemoteGhost(
  equipmentId: string,
  position: { x: number; y: number } | null
): void {
  useCanvasCollabStore.getState().setGhost(equipmentId, position);
}

export function shouldIgnoreRemoteDesign(row: {
  client_write_id?: string | null;
  updated_at?: string;
}): boolean {
  if (inFlight || queued) return true;
  if (consumePendingWriteId(row.client_write_id)) return true;
  const remoteAt = row.updated_at ? new Date(row.updated_at).getTime() : 0;
  if (lastLocalMutationAt && remoteAt && remoteAt < lastLocalMutationAt) {
    return true;
  }
  if (dragActiveFn()) return true;
  return false;
}

export function mergeById<T extends { id: string }>(local: T[], remote: T[]): T[] {
  const localMap = new Map(local.map((item) => [item.id, item]));
  const remoteMap = new Map(remote.map((item) => [item.id, item]));
  const ids = Array.from(localMap.keys());
  remoteMap.forEach((_, id) => {
    if (!localMap.has(id)) ids.push(id);
  });
  const out: T[] = [];
  ids.forEach((id) => {
    const localItem = localMap.get(id);
    const remoteItem = remoteMap.get(id);
    if (localItem) out.push(localItem);
    else if (remoteItem) out.push(remoteItem);
  });
  return out;
}
